import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Tabs,
  Tab,
  Grid,
  IconButton,
  Tooltip,
  Divider,
  LinearProgress,
  Snackbar,
  Backdrop,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  SwipeableDrawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Skeleton,
  Fade,
  Slide,
  alpha,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Stack
} from '@mui/material';
import {
  School as SchoolIcon,
  Assessment as AssessmentIcon,
  Article as ArticleIcon,
  Refresh as RefreshIcon,
  LogoutOutlined,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  GavelOutlined as GavelIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  Home as HomeIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  Event as EventIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

import ArguenteDashboardService from '../../services/ArguenteDashboardService';
import DefesaList from '../../components/Defesa/DefesaList';
import AvaliacaoList from '../../components/Avaliacao/AvaliacaoList';
import AvaliacoesPendentes from '../../components/Avaliacao/AvaliacoesPendentes';
import MonografiaList from '../../components/Monografia/MonografiaList';
import Footer from '../../components/Footer/Footer';
import { useRole } from '../../contexts/RoleContext';



import useAuth from '../../hooks/useAuth';

// ===== HOOKS PERSONALIZADOS PARA MOBILE =====
const useIsMobile = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return { isMobile, isTablet, isSmallMobile };
};

const useSwipeGesture = (onSwipeLeft, onSwipeRight) => {
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && onSwipeLeft) onSwipeLeft();
    if (isRightSwipe && onSwipeRight) onSwipeRight();
  };

  return { onTouchStart, onTouchMove, onTouchEnd };
};

const useDebounce = (callback, delay) => {
  const [debounceTimer, setDebounceTimer] = useState(null);

  const debouncedCallback = useCallback((...args) => {
    if (debounceTimer) clearTimeout(debounceTimer);
    
    const timer = setTimeout(() => {
      callback(...args);
    }, delay);
    
    setDebounceTimer(timer);
  }, [callback, delay, debounceTimer]);

  useEffect(() => {
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [debounceTimer]);

  return debouncedCallback;
};

// ===== COMPONENTE DE NAVEGAÇÃO MOBILE =====
const MobileNavigation = ({ 
  activeTab, 
  onTabChange, 
  drawerOpen, 
  onDrawerToggle,
  notificationCount = 0,
  userName,
  onLogout 
}) => {
  const { isMobile } = useIsMobile();

  const menuItems = [
    { label: 'Dashboard', icon: <GavelIcon />, tab: -1 },
    { label: 'Monografias', icon: <ArticleIcon />, tab: 0 },
    { label: 'Defesas', icon: <SchoolIcon />, tab: 1 },
    { label: 'Avaliações', icon: <AssessmentIcon />, tab: 2 }
  ];

  const handleMenuClick = (tab) => {
    if (tab === -1) {
      onTabChange(null, 0);
    } else {
      onTabChange(null, tab);
    }
    onDrawerToggle(false);
  };

  return (
    <>
      {isMobile && (
        <AppBar position="sticky" sx={{ zIndex: 1300 }}>
          <Toolbar sx={{ minHeight: 64 }}>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => onDrawerToggle(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            
            <Typography variant="h6" sx={{ flexGrow: 1, fontSize: '1.1rem' }}>
              Arguente
            </Typography>

            <IconButton color="inherit">
              <Badge badgeContent={notificationCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>

            <IconButton 
              color="inherit"
              onClick={onLogout}
              edge="end"
            >
              <LogoutOutlined />
            </IconButton>
          </Toolbar>
        </AppBar>
      )}

      <SwipeableDrawer
        anchor="left"
        open={drawerOpen}
        onClose={() => onDrawerToggle(false)}
        onOpen={() => onDrawerToggle(true)}
        disableBackdropTransition
        disableDiscovery
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box',
          },
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              {userName?.charAt(0).toUpperCase() || 'A'}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {userName || 'Arguente'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Arguente
              </Typography>
            </Box>
          </Box>
        </Box>

        <List sx={{ pt: 1 }}>
          {menuItems.map((item, index) => (
            <ListItemButton 
              key={index}
              selected={activeTab === item.tab}
              onClick={() => handleMenuClick(item.tab)}
              sx={{
                borderRadius: 1,
                mx: 1,
                mb: 0.5,
                '&.Mui-selected': {
                  bgcolor: 'primary.50',
                  '&:hover': {
                    bgcolor: 'primary.100',
                  }
                }
              }}
            >
              <ListItemIcon sx={{ color: activeTab === item.tab ? 'primary.main' : 'inherit' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.label}
                sx={{ 
                  '& .MuiListItemText-primary': {
                    fontWeight: activeTab === item.tab ? 'bold' : 'normal',
                    color: activeTab === item.tab ? 'primary.main' : 'inherit'
                  }
                }}
              />
            </ListItemButton>
          ))}
        </List>

        <Box sx={{ mt: 'auto', p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<LogoutOutlined />}
            onClick={onLogout}
            color="error"
          >
            Sair
          </Button>
        </Box>
      </SwipeableDrawer>
    </>
  );
};

MobileNavigation.propTypes = {
  activeTab: PropTypes.number.isRequired,
  onTabChange: PropTypes.func.isRequired,
  drawerOpen: PropTypes.bool.isRequired,
  onDrawerToggle: PropTypes.func.isRequired,
  notificationCount: PropTypes.number,
  userName: PropTypes.string,
  onLogout: PropTypes.func.isRequired
};

// ===== COMPONENTE CARD EXPANSÍVEL =====
const ExpandableCard = ({ 
  children, 
  title, 
  subtitle, 
  status, 
  statusColor, 
  isNew, 
  actions,
  expanded = false,
  onExpand,
  ...props 
}) => {
  const { isMobile, isSmallMobile } = useIsMobile();
  const [isExpanded, setIsExpanded] = useState(!isMobile || expanded);

  useEffect(() => {
    if (!isMobile) {
      setIsExpanded(true);
    } else {
      setIsExpanded(expanded);
    }
  }, [isMobile, expanded]);

  const handleExpand = () => {
    if (isMobile) {
      setIsExpanded(!isExpanded);
      if (onExpand) onExpand(!isExpanded);
    }
  };

  return (
    <Card 
      sx={{ 
        height: isMobile ? 'auto' : '100%',
        border: isNew ? '2px solid #2196f3' : '1px solid rgba(0,0,0,0.12)',
        boxShadow: isNew ? '0 4px 8px rgba(33, 150, 243, 0.3)' : undefined,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: 6,
          transform: isMobile ? 'none' : 'translateY(-2px)'
        }
      }}
      {...props}
    >
      {isMobile ? (
        <Accordion expanded={isExpanded} onChange={handleExpand}>
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{ 
              minHeight: 64,
              '& .MuiAccordionSummary-content': {
                alignItems: 'center',
                gap: 1
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography 
                  variant={isSmallMobile ? "subtitle2" : "h6"} 
                  sx={{ 
                    fontWeight: 'bold',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {title}
                </Typography>
                {subtitle && (
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ 
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {subtitle}
                  </Typography>
                )}
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {isNew && (
                  <Chip
                    label="NOVO"
                    size="small"
                    color="primary"
                    sx={{ 
                      fontWeight: 'bold',
                      fontSize: '0.7rem',
                      height: 20
                    }}
                  />
                )}
                {status && (
                  <Chip
                    label={status}
                    color={statusColor}
                    size="small"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                )}
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {children}
            {actions && (
              <Box sx={{ 
                mt: 2, 
                pt: 2, 
                borderTop: 1, 
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                gap: 1
              }}>
                {actions}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      ) : (
        <>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    {title}
                  </Typography>
                  {isNew && (
                    <Chip
                      label="NOVO"
                      size="small"
                      color="primary"
                      sx={{ fontWeight: 'bold' }}
                    />
                  )}
                </Box>
                {status && (
                  <Chip
                    label={status}
                    color={statusColor}
                    size="small"
                    sx={{ mb: 2 }}
                  />
                )}
              </Box>
            </Box>
            {children}
          </CardContent>
          {actions && (
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
              {actions}
            </Box>
          )}
        </>
      )}
    </Card>
  );
};

ExpandableCard.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  status: PropTypes.string,
  statusColor: PropTypes.string,
  isNew: PropTypes.bool,
  actions: PropTypes.node,
  expanded: PropTypes.bool,
  onExpand: PropTypes.func
};

// ===== COMPONENTE HEADER DESKTOP =====
const DashboardHeader = ({ userName, onRefresh, refreshing, onLogout }) => {
  const theme = useTheme();
  const { isMobile } = useIsMobile();
  const [anchorElProfile, setAnchorElProfile] = useState(null);
  const [anchorElNotifications, setAnchorElNotifications] = useState(null);

  const handleProfileMenuOpen = (event) => {
    setAnchorElProfile(event.currentTarget);
  };

  const handleNotificationsOpen = (event) => {
    setAnchorElNotifications(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorElProfile(null);
    setAnchorElNotifications(null);
  };

  if (isMobile) return null;

  return (
    <AppBar 
      position="sticky" 
      sx={{ 
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        boxShadow: theme.shadows[4]
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', minHeight: '72px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <GavelIcon sx={{ fontSize: 40, color: 'white' }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
              Painel do Arguente
            </Typography>
            <Typography variant="body2" sx={{ color: alpha('#ffffff', 0.8) }}>
              Bem-vindo, {userName}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            color="inherit"
            onClick={onRefresh}
            disabled={refreshing}
            sx={{
              bgcolor: alpha('#ffffff', 0.1),
              '&:hover': { bgcolor: alpha('#ffffff', 0.2) },
              transition: 'all 0.3s ease-in-out'
            }}
          >
            {refreshing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
          </IconButton>

          <IconButton
            color="inherit"
            onClick={handleNotificationsOpen}
            sx={{
              bgcolor: alpha('#ffffff', 0.1),
              '&:hover': { bgcolor: alpha('#ffffff', 0.2) },
              transition: 'all 0.3s ease-in-out'
            }}
          >
            <Badge badgeContent={3} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
            <Avatar 
              sx={{ 
                bgcolor: alpha('#ffffff', 0.2),
                color: 'white',
                width: 40,
                height: 40,
                fontWeight: 600
              }}
            >
              {userName?.charAt(0)?.toUpperCase() || 'A'}
            </Avatar>
            <IconButton
              color="inherit"
              onClick={handleProfileMenuOpen}
              sx={{
                bgcolor: alpha('#ffffff', 0.1),
                '&:hover': { bgcolor: alpha('#ffffff', 0.2) },
                transition: 'all 0.3s ease-in-out'
              }}
            >
              <AccountCircleIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Menus */}
        <Menu
          anchorEl={anchorElProfile}
          open={Boolean(anchorElProfile)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleMenuClose}>
            <ListItemIcon><AccountCircleIcon /></ListItemIcon>
            Meu Perfil
          </MenuItem>
          <Divider />
          <MenuItem onClick={onLogout} sx={{ color: 'error.main' }}>
            <ListItemIcon><LogoutOutlined color="error" /></ListItemIcon>
            Sair
          </MenuItem>
        </Menu>

        <Menu
          anchorEl={anchorElNotifications}
          open={Boolean(anchorElNotifications)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: { minWidth: 300, maxHeight: 400 }
          }}
        >
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">Notificações</Typography>
          </Box>
          <MenuItem>
            <Typography variant="body2">Nova defesa agendada</Typography>
          </MenuItem>
          <MenuItem>
            <Typography variant="body2">Avaliação pendente</Typography>
          </MenuItem>
          <MenuItem>
            <Typography variant="body2">Monografia disponível</Typography>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

DashboardHeader.propTypes = {
  userName: PropTypes.string,
  onRefresh: PropTypes.func.isRequired,
  refreshing: PropTypes.bool.isRequired,
  onLogout: PropTypes.func.isRequired
};

// ===== COMPONENTE DE NOTIFICAÇÃO OTIMIZADO =====
const NotificationSnackbar = ({ open, message, severity, onClose }) => {
  const { isMobile } = useIsMobile();
  
  const getIcon = () => {
    switch (severity) {
      case 'success': return <CheckCircleIcon />;
      case 'error': return <ErrorIcon />;
      case 'warning': return <WarningIcon />;
      case 'info': return <InfoIcon />;
      default: return <InfoIcon />;
    }
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={6000}
      onClose={onClose}
      anchorOrigin={{ 
        vertical: isMobile ? 'bottom' : 'top', 
        horizontal: isMobile ? 'center' : 'right' 
      }}
    >
      <Alert 
        onClose={onClose} 
        severity={severity} 
        sx={{ 
          width: '100%',
          minWidth: isMobile ? '300px' : '300px',
          fontSize: isMobile ? '16px' : '14px',
          '& .MuiAlert-icon': {
            fontSize: isMobile ? '24px' : '20px'
          }
        }}
        icon={getIcon()}
      >
        {message}
      </Alert>
    </Snackbar>
  );
};

NotificationSnackbar.propTypes = {
  open: PropTypes.bool.isRequired,
  message: PropTypes.string.isRequired,
  severity: PropTypes.oneOf(['success', 'error', 'warning', 'info']).isRequired,
  onClose: PropTypes.func.isRequired
};

// ===== LOADING SKELETON =====
const LoadingSkeleton = () => {
  const { isMobile } = useIsMobile();
  
  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      <Skeleton 
        variant="rectangular" 
        height={60} 
        sx={{ mb: 3, borderRadius: 2 }} 
      />
      <Grid container spacing={isMobile ? 2 : 3}>
        {[1,2,3].map(item => (
          <Grid item xs={12} md={4} key={item}>
            <Skeleton 
              variant="rectangular" 
              height={120} 
              sx={{ borderRadius: 2 }} 
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// ===== COMPONENTE PRINCIPAL =====
function ArguenteDashboardPage() {
  const theme = useTheme();
  const navigate = useNavigate();
 const { currentRole } = useRole();
const { user, logout } = useAuth();
const effectiveRole = currentRole && currentRole !== 'auto' ? currentRole : (user?.tipo || '');
  const { isMobile, isSmallMobile } = useIsMobile();

  // Estados principais
  const [activeTab, setActiveTab] = useState(0);
  const [dashboardData, setDashboardData] = useState({
    monografias: [],
    defesas: [],
    avaliacoes: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Estados para notificações e ações
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [submittingAction, setSubmittingAction] = useState(false);

  // Hooks personalizados
  const swipeGestures = useSwipeGesture(
    () => {
      if (drawerOpen) setDrawerOpen(false);
      else if (activeTab < 2) setActiveTab(activeTab + 1);
    },
    () => {
      if (!drawerOpen) setDrawerOpen(true);
      else if (activeTab > 0) setActiveTab(activeTab - 1);
    }
  );

  // Funções de notificação
  const showNotification = useCallback((message, severity = 'info') => {
    setNotification({
      open: true,
      message,
      severity
    });
  }, []);

  const handleCloseNotification = useCallback(() => {
    setNotification(prev => ({ ...prev, open: false }));
  }, []);

  const debouncedRefresh = useDebounce(async () => {
    setRefreshing(true);
    await fetchDashboardData();
    setRefreshing(false);
  }, 300);

  // Funções para documentos (mantidas do original)
  const handleDownloadDocument = useCallback(async (documento, tipo) => {
    try {
      showNotification('Iniciando download do documento...', 'info');
      
      let fileUrl = null;
      let fileName = null;

      if (tipo === 'monografia') {
        fileUrl = documento.documento_monografia || documento.arquivo_url || documento.documento_url;
        fileName = `monografia_${documento.id}_${(documento.titulo || documento.projeto?.proposta?.titulo)?.substring(0, 20) || 'documento'}`;
      } else if (tipo === 'defesa') {
        fileUrl = documento.documento_defesa || documento.arquivo_url;
        fileName = `defesa_${documento.id}_${(documento.titulo || documento.monografia?.titulo)?.substring(0, 20) || 'documento'}`;
      } else if (tipo === 'avaliacao') {
        fileUrl = documento.documento_avaliacao || documento.arquivo_url;
        fileName = `avaliacao_${documento.id}_${documento.titulo?.substring(0, 20) || 'documento'}`;
      }

      if (!fileUrl) {
        showNotification('Documento não encontrado para download.', 'warning');
        return;
      }

      const fileNameFromPath = fileUrl.split('\\').pop().split('/').pop();
     const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://api.zenkroll.co.mz';
const apiUrl = `${baseUrl}/api/documentos/${fileNameFromPath}`;

      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('Sessão expirada. Faça login novamente.', 'error');
        return;
      }

      showNotification('Processando download...', 'info');

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        showNotification('Sessão expirada. Faça login novamente.', 'error');
        return;
      }

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status} - ${response.statusText}`);
      }

      const blob = await response.blob();

      if (blob.size === 0) {
        throw new Error('Arquivo vazio recebido do servidor.');
      }

      if (blob.type && (blob.type.includes('text/html') || blob.type.includes('application/json'))) {
        throw new Error('Servidor retornou dados incorretos em vez do arquivo.');
      }

      const originalExtension = fileNameFromPath.split('.').pop();
      const finalFileName = `${fileName}.${originalExtension}`;

      const objectUrl = URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');
      downloadLink.href = objectUrl;
      downloadLink.download = finalFileName;
      downloadLink.style.display = 'none';
      
      document.body.appendChild(downloadLink);
      downloadLink.click();
      
      setTimeout(() => {
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(objectUrl);
      }, 100);

      showNotification(`Download concluído: ${finalFileName}`, 'success');

    } catch (error) {
      console.error('Erro geral no download:', error);
      showNotification(`Erro ao baixar documento: ${error.message}`, 'error');
    }
  }, [showNotification]);

  const handleViewDocument = useCallback(async (documento, tipo) => {
    try {
      showNotification('Abrindo documento...', 'info');
      
      let fileUrl = null;
      
      if (tipo === 'monografia') {
        fileUrl = documento.documento_monografia || documento.arquivo_url || documento.documento_url;
      } else if (tipo === 'defesa') {
        fileUrl = documento.documento_defesa || documento.arquivo_url;
      } else if (tipo === 'avaliacao') {
        fileUrl = documento.documento_avaliacao || documento.arquivo_url;
      }

      if (!fileUrl) {
        showNotification('Documento não disponível para visualização.', 'warning');
        return;
      }

      const fileNameFromPath = fileUrl.split('\\').pop().split('/').pop();
      const fileExtension = fileNameFromPath.split('.').pop().toLowerCase();
    const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://api.zenkroll.co.mz';
const publicUrl = `${baseUrl}/api/documentos/view/${fileNameFromPath}`;

      if (fileExtension === 'pdf') {
        window.open(publicUrl, '_blank');
        showNotification('Documento PDF aberto em nova aba!', 'success');
      } else if (['docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt'].includes(fileExtension)) {
        const userChoice = window.confirm(
          `Documento: ${fileNameFromPath}\n\n` +
          `Os documentos Word/Excel não podem ser visualizados diretamente no navegador.\n\n` +
          `Clique OK para baixar e abrir no seu computador.\n` +
          `Clique Cancelar para tentar visualizar online (pode não funcionar).`
        );
        
        if (userChoice) {
          handleDownloadDocument(documento, tipo);
        } else {
          const googleViewerUrl = `https://docs.google.com/gviewer?url=${encodeURIComponent(publicUrl)}&embedded=true`;
          window.open(googleViewerUrl, '_blank', 'width=1200,height=800');
          showNotification('Tentando visualizar documento online...', 'info');
        }
      } else {
        window.open(publicUrl, '_blank');
     showNotification('Documento aberto em nova aba!', 'success');
      }

    } catch (error) {
      console.error('Erro ao visualizar documento:', error);
      showNotification(`Erro ao abrir documento: ${error.message}`, 'error');
    }
  }, [showNotification, handleDownloadDocument]);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      showNotification('Carregando dados do painel...', 'info');
      
      const fetchedData = await ArguenteDashboardService.getDadosArguente();
      console.log('Dados do dashboard recebidos (arguente):', fetchedData);
      setDashboardData({
        monografias: fetchedData.monografias || [],
        defesas: fetchedData.defesas || [],
        avaliacoes: fetchedData.avaliacoes || []
      });
      setError(null);
      showNotification('Dados carregados com sucesso!', 'success');
    } catch (err) {
      console.error('Erro ao carregar dados do dashboard (arguente):', err);
      setError('Não foi possível carregar os dados do dashboard.');
      showNotification('Erro ao carregar dados. Tente novamente.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

 useEffect(() => {
  if (effectiveRole === 'arguente') {
    fetchDashboardData();
  }
}, [effectiveRole, fetchDashboardData]);


const handleLogout = useCallback(() => {
  showNotification('Encerrando sessão...', 'info');
  logout();
  navigate('/login');
}, [showNotification, navigate]);
  // Renderização dos cards de resumo otimizada para mobile
  const renderSummaryCards = () => {
    const summaryData = [
      {
        title: 'Monografias para Arguir',
        value: (dashboardData.monografias || []).filter(m => m.status === 'EM_REVISAO' || m.status === 'APROVADA').length,
        total: (dashboardData.monografias || []).length,
        icon: <ArticleIcon />,
        color: '#1976d2'
      },
      {
        title: 'Defesas Pendentes',
        value: (dashboardData.defesas || []).filter(d => d.status === 'PENDENTE').length,
        total: (dashboardData.defesas || []).length,
        icon: <SchoolIcon />,
        color: '#2e7d32'
      },
      {
        title: 'Avaliações a Realizar',
        value: (dashboardData.avaliacoes || []).filter(a => a.status === 'PENDENTE').length,
        total: (dashboardData.avaliacoes || []).length,
        icon: <AssessmentIcon />,
        color: '#ed6c02'
      }
    ];

    return (
      <Box sx={{ mb: isMobile ? 3 : 4 }}>
        {isMobile ? (
          // Layout mobile com cards em coluna
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {summaryData.map((item, index) => (
              <Card
                key={index}
                sx={{
                  background: `linear-gradient(135deg, ${item.color}15 0%, ${item.color}30 100%)`,
                  border: `1px solid ${item.color}30`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: 4
                  }
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ color: item.color }}>
                      {item.icon}
                    </Box>
                    <Typography variant="h4" component="div" sx={{ color: item.color, fontWeight: 'bold' }}>
                      {item.value}
                    </Typography>
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: item.color }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    de {item.total} total
                  </Typography>
                  {item.total > 0 && (
                    <LinearProgress
                      variant="determinate"
                      value={(item.value / item.total) * 100}
                      sx={{ 
                        mt: 1, 
                        height: 6, 
                        borderRadius: 3,
                        bgcolor: alpha(item.color, 0.2),
                        '& .MuiLinearProgress-bar': {
                          bgcolor: item.color
                        }
                      }}
                    />
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          // Grid no desktop
          <Grid container spacing={3}>
            {summaryData.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    '&:hover': {
                      boxShadow: 6,
                      transform: 'translateY(-2px)',
                      transition: 'all 0.3s'
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          bgcolor: `${item.color}20`,
                          color: item.color,
                          mr: 2
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Typography variant="h6" color="text.secondary">
                        {item.title}
                      </Typography>
                    </Box>
                    <Typography variant="h4" component="div" color="primary">
                      {item.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      de {item.total} total
                    </Typography>
                    {item.total > 0 && (
                      <LinearProgress
                        variant="determinate"
                        value={(item.value / item.total) * 100}
                        sx={{ mt: 1, height: 6, borderRadius: 3 }}
                        color={item.value === 0 ? 'success' : 'warning'}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    );
  };

  // Renderização das próximas defesas otimizada para mobile
  const renderNextDefesas = () => {
    const proximasDefesas = (dashboardData.defesas || [])
      .filter(defesa => new Date(defesa.dataDefesa) > new Date())
      .sort((a, b) => new Date(a.dataDefesa) - new Date(b.dataDefesa))
      .slice(0, isMobile ? 2 : 3);

    if (proximasDefesas.length === 0) return null;

    return (
      <Paper sx={{ p: isMobile ? 2 : 3, mb: isMobile ? 3 : 4 }}>
        <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          textAlign: isMobile ? 'center' : 'left',
          justifyContent: isMobile ? 'center' : 'flex-start'
        }}>
          <EventIcon />
          Próximas Defesas como Arguente
          <Chip size="small" label={proximasDefesas.length} color="primary" />
        </Typography>
        
        <Grid container spacing={2}>
          {proximasDefesas.map((defesa, index) => (
            <Grid item xs={12} sm={isMobile ? 12 : 4} key={index}>
              <ExpandableCard
                title={format(new Date(defesa.dataDefesa), "dd 'de' MMMM', às' HH:mm", { locale: ptBR })}
                subtitle={defesa.titulo || 'Título da defesa'}
                status={defesa.status}
                statusColor={defesa.status === 'PENDENTE' ? 'warning' : 'success'}
                actions={
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: isMobile ? 'center' : 'flex-end' }}>
                    <IconButton
                      size="small"
                      onClick={() => handleViewDocument(defesa, 'defesa')}
                      disabled={!defesa.documento_defesa && !defesa.arquivo_url}
                      sx={{ bgcolor: 'primary.50' }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDownloadDocument(defesa, 'defesa')}
                      disabled={!defesa.documento_defesa && !defesa.arquivo_url}
                      sx={{ bgcolor: 'secondary.50' }}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Box>
                }
              />
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  };

  // Componente de tabs otimizada para mobile
  const EnhancedTabs = ({ value, onChange, tabs }) => (
    <Box sx={{ mb: isMobile ? 2 : 0 }}>
      {isMobile ? (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={value}
            onChange={onChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTab-root': {
                minWidth: 90,
                fontSize: '0.875rem',
                px: 1,
                minHeight: 48,
                textTransform: 'none',
                fontWeight: 500
              }
            }}
          >
            {tabs.map((tab, index) => (
              <Tab 
                key={index}
                icon={React.cloneElement(tab.icon, { fontSize: 'small' })}
                label={tab.label}
                iconPosition="top"
                sx={{
                  '&.Mui-selected': {
                    color: 'primary.main',
                    fontWeight: 'bold'
                  }
                }}
              />
            ))}
          </Tabs>
        </Box>
      ) : (
        <Tabs
          value={value}
          onChange={onChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
          variant="fullWidth"
        >
          {tabs.map((tab, index) => (
            <Tab 
              key={index}
              label={tab.label} 
              icon={tab.icon} 
              iconPosition="start"
            />
          ))}
        </Tabs>
      )}
    </Box>
  );

  EnhancedTabs.propTypes = {
    value: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    tabs: PropTypes.arrayOf(PropTypes.shape({
      icon: PropTypes.element.isRequired,
      label: PropTypes.string.isRequired
    })).isRequired
  };

  // Renderizações das abas principais otimizadas para mobile
  const renderMonografias = () => (
    <Card sx={{ borderRadius: isMobile ? 1 : 2 }}>
      <CardContent sx={{ p: isMobile ? 2 : 3 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'stretch' : 'center', 
          mb: 3,
          gap: isMobile ? 2 : 0
        }}>
          <Typography variant={isMobile ? "h6" : "h6"} sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            textAlign: isMobile ? 'center' : 'left'
          }}>
            <ArticleIcon />
            Monografias para Arguição
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: isMobile ? 'center' : 'flex-end' }}>
            <Button
              variant="outlined"
              size={isMobile ? "medium" : "small"}
              startIcon={<RefreshIcon />}
              onClick={() => {
                showNotification('Atualizando monografias...', 'info');
                fetchDashboardData();
              }}
              fullWidth={isMobile}
            >
              Atualizar
            </Button>
          </Box>
        </Box>
        
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Como arguente, você tem a responsabilidade de avaliar criticamente as monografias durante as defesas.
          </Typography>
        </Alert>

        <MonografiaList 
          data={dashboardData.monografias}
          onUpdate={fetchDashboardData}
          userRole="arguente"
          onViewDocument={(item) => handleViewDocument(item, 'monografia')}
          onDownloadDocument={(item) => handleDownloadDocument(item, 'monografia')}
          showNotification={showNotification}
        />
      </CardContent>
    </Card>
  );

  const renderDefesas = () => (
    <Card sx={{ borderRadius: isMobile ? 1 : 2 }}>
      <CardContent sx={{ p: isMobile ? 2 : 3 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'stretch' : 'center', 
          mb: 3,
          gap: isMobile ? 2 : 0
        }}>
          <Typography variant={isMobile ? "h6" : "h6"} sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            textAlign: isMobile ? 'center' : 'left'
          }}>
            <SchoolIcon />
            Defesas como Arguente
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: isMobile ? 'center' : 'flex-end' }}>
            <Button
              variant="outlined"
              size={isMobile ? "medium" : "small"}
              startIcon={<RefreshIcon />}
              onClick={() => {
                showNotification('Atualizando defesas...', 'info');
                fetchDashboardData();
              }}
              fullWidth={isMobile}
            >
              Atualizar
            </Button>
          </Box>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Acompanhe e prepare-se para as defesas onde você atuará como membro arguente do Juri.
          </Typography>
        </Alert>

        <DefesaList 
          data={dashboardData.defesas}
          onUpdate={fetchDashboardData}
          userRole="arguente"
          onViewDocument={(item) => handleViewDocument(item, 'defesa')}
          onDownloadDocument={(item) => handleDownloadDocument(item, 'defesa')}
          showNotification={showNotification}
        />
      </CardContent>
    </Card>
  );

  const renderAvaliacoes = () => (
    <Card sx={{ borderRadius: isMobile ? 1 : 2 }}>
      <CardContent sx={{ p: isMobile ? 2 : 3 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'stretch' : 'center', 
          mb: 3,
          gap: isMobile ? 2 : 0
        }}>
          <Typography variant={isMobile ? "h6" : "h6"} sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            textAlign: isMobile ? 'center' : 'left'
          }}>
            <AssessmentIcon />
            Avaliações como Arguente
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: isMobile ? 'center' : 'flex-end' }}>
            <Button
              variant="outlined"
              size={isMobile ? "medium" : "small"}
              startIcon={<RefreshIcon />}
              onClick={() => {
                showNotification('Atualizando avaliações...', 'info');
                fetchDashboardData();
              }}
              fullWidth={isMobile}
            >
              Atualizar
            </Button>
          </Box>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Realize suas avaliações como membro arguente dos Juri.
          </Typography>
        </Alert>

        <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          textAlign: isMobile ? 'center' : 'left'
        }}>
          <InfoIcon />
          Avaliações Pendentes
        </Typography>
        <AvaliacoesPendentes 
          userRole="arguente"
          showNotification={showNotification}
        />
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          textAlign: isMobile ? 'center' : 'left'
        }}>
          <AssessmentIcon />
          Todas as Avaliações
        </Typography>
        <AvaliacaoList 
          data={dashboardData.avaliacoes}
          onUpdate={fetchDashboardData}
          userRole="arguente"
          onViewDocument={(item) => handleViewDocument(item, 'avaliacao')}
          onDownloadDocument={(item) => handleDownloadDocument(item, 'avaliacao')}
          showNotification={showNotification}
        />
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: isMobile ? '50vh' : '400px',
        p: isMobile ? 2 : 3
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={isMobile ? 60 : 50} />
          <Typography variant={isMobile ? "h6" : "h6"} color="text.secondary">
            Carregando painel do arguente...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: alpha(theme.palette.primary.main, 0.02),
      pb: isMobile ? 10 : 0
    }}
    {...swipeGestures}
    >
      {/* Navegação Mobile */}
      <MobileNavigation
        activeTab={activeTab}
        onTabChange={setActiveTab}
        drawerOpen={drawerOpen}
        onDrawerToggle={setDrawerOpen}
        notificationCount={3}
        userName={user?.nome}
        onLogout={handleLogout}
      />

      {/* Header Desktop */}
      <DashboardHeader
        userName={user?.nome}
        onRefresh={debouncedRefresh}
        refreshing={refreshing}
        onLogout={handleLogout}
      />

      {/* Container Principal */}
      <Box sx={{ maxWidth: isMobile ? '100%' : 1200, mx: 'auto', p: isMobile ? 1 : 3 }}>
        {/* Cabeçalho mobile alternativo */}
        {isMobile && (
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <GavelIcon color="primary" />
              Painel do Arguente
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Bem-vindo, {user?.nome}
            </Typography>
          </Box>
        )}

        {/* Mensagem de erro */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }}
            action={
              <Button 
                color="inherit" 
                size={isMobile ? "medium" : "small"} 
                onClick={debouncedRefresh}
              >
                Tentar Novamente
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Indicador de refresh */}
        {refreshing && (
          <Box sx={{ mb: 3 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
              Atualizando dados...
            </Typography>
          </Box>
        )}

        {/* Cards de resumo */}
        {renderSummaryCards()}

        {/* Próximas defesas */}
        {renderNextDefesas()}

        {/* Speed Dial para mobile */}
        {isMobile && (
          <SpeedDial
            ariaLabel="Ações rápidas"
            sx={{ 
              position: 'fixed', 
              bottom: 80, 
              right: 16,
              zIndex: 1000
            }}
            icon={<SpeedDialIcon />}
            direction="up"
          >
            <SpeedDialAction
              icon={<RefreshIcon />}
              tooltipTitle="Atualizar"
              onClick={debouncedRefresh}
            />
            <SpeedDialAction
              icon={<NotificationsIcon />}
              tooltipTitle="Notificações"
              onClick={() => {}}
            />
            <SpeedDialAction
              icon={<FilterIcon />}
              tooltipTitle="Filtros"
              onClick={() => {}}
            />
          </SpeedDial>
        )}

        {/* Tabs e conteúdo */}
        <Paper sx={{ mb: isMobile ? 2 : 4, borderRadius: isMobile ? 1 : 2 }}>
          <EnhancedTabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            tabs={[
              { label: "Monografias", icon: <ArticleIcon /> },
              { label: "Defesas", icon: <SchoolIcon /> },
              { label: "Avaliações", icon: <AssessmentIcon /> }
            ]}
          />

          <Box sx={{ p: 0 }}>
            {activeTab === 0 && renderMonografias()}
            {activeTab === 1 && renderDefesas()}
            {activeTab === 2 && renderAvaliacoes()}
          </Box>
        </Paper>
      </Box>

      {/* Backdrop para loading de operações */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={submittingAction}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: isMobile ? 3 : 2,
          p: isMobile ? 3 : 2,
          bgcolor: alpha('#000000', 0.8),
          borderRadius: 2
        }}>
          <CircularProgress color="inherit" size={isMobile ? 60 : 40} />
          <Typography variant={isMobile ? "h6" : "h6"}>
            Processando solicitação...
          </Typography>
          <Typography variant="body2" sx={{ textAlign: 'center', maxWidth: 300 }}>
            Por favor, aguarde enquanto processamos sua solicitação.
          </Typography>
        </Box>
      </Backdrop>

      {/* Sistema de notificações */}
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={handleCloseNotification}
      />

      {/* Footer - apenas no desktop */}
      {!isMobile && <Footer />}
    </Box>
  );
}

export default ArguenteDashboardPage;